import { calculatePortfolioDrawdown } from "./portfolioDrawdown";
import { calculateEtfOverlap } from "./etfOverlap";
import { calculatePortfolioConcentration, exampleHoldings } from "./portfolioConcentration";

export type CalculatorSlug =
  | "etf-stock-overlap"
  | "portfolio-concentration"
  | "portfolio-drawdown"
  | "compound-interest"
  | "inflation"
  | "savings-goal"
  | "investment-fees"
  | "mortgage-payment"
  | "private-mortgage-insurance"
  | "car-loan-payment"
  | "investment-return"
  | "credit-card-payoff"
  | "debt-payoff"
  | "retirement-401k"
  | "net-worth"
  | "emergency-fund"
  | "mortgage-affordability"
  | "mortgage-refinance"
  | "rent-vs-buy"
  | "student-loan-payoff";

export type CalculatorCategory = "Grow" | "Plan" | "Borrow";

export type CalculatorField = {
  key: string;
  label: string;
  defaultValue: number;
  min: number;
  max: number;
  step: number;
  prefix?: string;
  suffix?: string;
  helper?: string;
};

export type CalculatorStat = {
  label: string;
  value: string;
  detail?: string;
  tone?: "positive" | "negative" | "neutral";
};

export type CalculatorChart =
  | {
      kind: "line";
      points: Array<{ label: string; value: number }>;
      valueLabel: string;
    }
  | {
      kind: "breakdown";
      segments: Array<{ label: string; value: number; tone: "accent" | "blue" | "orange" | "purple" | "negative" }>;
      valueLabel: string;
    };

export type CalculatorResult = {
  headlineLabel: string;
  headlineValue: string;
  headlineDetail: string;
  headlineTone?: "positive" | "negative" | "neutral";
  stats: CalculatorStat[];
  chart: CalculatorChart;
  note: string;
};

export type CalculatorDefinition = {
  slug: CalculatorSlug;
  category: CalculatorCategory;
  name: string;
  title: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  fields: CalculatorField[];
  methodTitle: string;
  method: string;
  disclaimer: string;
  faq: Array<{ question: string; answer: string }>;
  related: CalculatorSlug[];
  guide?: { blogSlug: string; title: string };
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const preciseCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function currency(value: number, precise = false) {
  return (precise ? preciseCurrencyFormatter : currencyFormatter).format(Math.max(0, Number.isFinite(value) ? value : 0));
}

function signedCurrency(value: number) {
  const amount = currency(Math.abs(value));
  return value < 0 ? `−${amount}` : `+${amount}`;
}

function percentage(value: number) {
  return `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(1)}%`;
}

function field(key: string, label: string, defaultValue: number, min: number, max: number, step: number, options: Pick<CalculatorField, "prefix" | "suffix" | "helper"> = {}): CalculatorField {
  return { key, label, defaultValue, min, max, step, ...options };
}

const compoundInterestFields = [
  field("initial", "Starting balance", 10_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("monthlyContribution", "Monthly contribution", 500, 0, 1_000_000, 25, { prefix: "$" }),
  field("annualRate", "Annual rate", 7, 0, 30, 0.1, { suffix: "%" }),
  field("years", "Time horizon", 10, 1, 60, 1, { suffix: "years" }),
];

const inflationFields = [
  field("amount", "Amount today", 10_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("inflationRate", "Annual inflation", 3, 0, 20, 0.1, { suffix: "%" }),
  field("years", "Time horizon", 10, 1, 60, 1, { suffix: "years" }),
];

const savingsGoalFields = [
  field("target", "Savings target", 25_000, 1, 100_000_000, 100, { prefix: "$" }),
  field("current", "Already saved", 5_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("annualRate", "Annual rate", 4, 0, 30, 0.1, { suffix: "%" }),
  field("years", "Time horizon", 5, 1, 60, 1, { suffix: "years" }),
];

const investmentFeeFields = [
  field("initial", "Starting balance", 10_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("annualContribution", "Annual contribution", 6_000, 0, 10_000_000, 100, { prefix: "$" }),
  field("annualReturn", "Annual return", 7, 0, 30, 0.1, { suffix: "%" }),
  field("feeRate", "Annual fee", 0.5, 0, 10, 0.05, { suffix: "%" }),
  field("years", "Time horizon", 20, 1, 60, 1, { suffix: "years" }),
];

const mortgageFields = [
  field("homePrice", "Home price", 400_000, 1, 100_000_000, 1_000, { prefix: "$" }),
  field("downPayment", "Down payment", 80_000, 0, 100_000_000, 1_000, { prefix: "$" }),
  field("interestRate", "Interest rate", 6.5, 0, 25, 0.01, { suffix: "%" }),
  field("termYears", "Loan term", 30, 1, 40, 1, { suffix: "years" }),
  field("propertyTaxRate", "Property tax", 1.1, 0, 10, 0.05, { suffix: "% / year" }),
  field("pmiRate", "Private mortgage insurance", 0.5, 0, 3, 0.05, { suffix: "% / year", helper: "Use 0% if PMI does not apply" }),
  field("homeInsurance", "Home insurance", 1_800, 0, 100_000, 100, { prefix: "$", suffix: "/ year" }),
  field("hoa", "HOA dues", 0, 0, 10_000, 25, { prefix: "$", suffix: "/ month" }),
];

const privateMortgageInsuranceFields = [
  field("homePrice", "Home price", 400_000, 1, 100_000_000, 1_000, { prefix: "$" }),
  field("downPayment", "Down payment", 40_000, 0, 100_000_000, 1_000, { prefix: "$" }),
  field("pmiRate", "Annual PMI rate", 0.5, 0, 3, 0.05, { suffix: "% / year", helper: "Rates vary by borrower and lender" }),
];

const carLoanFields = [
  field("vehiclePrice", "Vehicle price", 35_000, 1, 5_000_000, 500, { prefix: "$" }),
  field("downPayment", "Down payment", 5_000, 0, 5_000_000, 500, { prefix: "$" }),
  field("tradeIn", "Trade-in value", 0, 0, 5_000_000, 500, { prefix: "$" }),
  field("salesTaxRate", "Sales tax", 7, 0, 20, 0.1, { suffix: "%" }),
  field("fees", "Taxes and fees", 1_000, 0, 100_000, 100, { prefix: "$" }),
  field("interestRate", "APR", 7.5, 0, 40, 0.01, { suffix: "%" }),
  field("termMonths", "Loan term", 60, 6, 120, 6, { suffix: "months" }),
];

const investmentReturnFields = [
  field("startingValue", "Starting value", 10_000, 1, 100_000_000, 100, { prefix: "$" }),
  field("endingValue", "Ending value", 20_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("years", "Time horizon", 5, 1, 60, 1, { suffix: "years" }),
];

const creditCardPayoffFields = [
  field("balance", "Card balance", 8_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("annualRate", "APR", 24.99, 0, 60, 0.01, { suffix: "%" }),
  field("minimumPayment", "Monthly payment", 250, 0, 1_000_000, 10, { prefix: "$" }),
  field("extraPayment", "Extra each month", 100, 0, 1_000_000, 10, { prefix: "$", helper: "Optional amount beyond the payment" }),
];

const debtPayoffFields = [
  field("debt1Balance", "Debt 1 balance", 6_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("debt1Rate", "Debt 1 APR", 24, 0, 60, 0.01, { suffix: "%" }),
  field("debt1Payment", "Debt 1 minimum", 180, 0, 1_000_000, 10, { prefix: "$" }),
  field("debt2Balance", "Debt 2 balance", 12_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("debt2Rate", "Debt 2 APR", 7, 0, 60, 0.01, { suffix: "%" }),
  field("debt2Payment", "Debt 2 minimum", 250, 0, 1_000_000, 10, { prefix: "$" }),
  field("debt3Balance", "Debt 3 balance", 0, 0, 100_000_000, 100, { prefix: "$", helper: "Optional third debt" }),
  field("debt3Rate", "Debt 3 APR", 0, 0, 60, 0.01, { suffix: "%" }),
  field("debt3Payment", "Debt 3 minimum", 0, 0, 1_000_000, 10, { prefix: "$" }),
  field("extraPayment", "Extra each month", 200, 0, 1_000_000, 10, { prefix: "$" }),
];

const retirementFields = [
  field("currentBalance", "Current retirement balance", 25_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("annualSalary", "Annual salary", 80_000, 0, 10_000_000, 1_000, { prefix: "$" }),
  field("contributionRate", "Your contribution", 8, 0, 100, 0.1, { suffix: "% of salary" }),
  field("employerMatchRate", "Employer match", 50, 0, 200, 1, { suffix: "% of contribution" }),
  field("salaryGrowth", "Salary growth", 3, 0, 20, 0.1, { suffix: "% / year" }),
  field("annualReturn", "Annual return", 7, 0, 30, 0.1, { suffix: "%" }),
  field("currentAge", "Current age", 30, 18, 80, 1, { suffix: "years" }),
  field("retirementAge", "Retirement age", 65, 19, 90, 1, { suffix: "years" }),
];

const netWorthFields = [
  field("cash", "Cash and savings", 10_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("investments", "Investments", 25_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("retirement", "Retirement accounts", 50_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("realEstate", "Real estate", 0, 0, 100_000_000, 1_000, { prefix: "$" }),
  field("vehicles", "Vehicles", 25_000, 0, 100_000_000, 500, { prefix: "$" }),
  field("otherAssets", "Other assets", 5_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("mortgageDebt", "Mortgage debt", 0, 0, 100_000_000, 1_000, { prefix: "$" }),
  field("creditCardDebt", "Credit card debt", 5_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("studentLoanDebt", "Student loan debt", 15_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("otherDebts", "Other debts", 0, 0, 100_000_000, 100, { prefix: "$" }),
];

const emergencyFundFields = [
  field("monthlyExpenses", "Essential monthly expenses", 4_000, 0, 1_000_000, 50, { prefix: "$" }),
  field("targetMonths", "Target months", 6, 1, 24, 1, { suffix: "months" }),
  field("currentSavings", "Current emergency savings", 5_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("monthlyContribution", "Monthly contribution", 500, 0, 1_000_000, 25, { prefix: "$" }),
];

const mortgageAffordabilityFields = [
  field("monthlyIncome", "Gross monthly income", 10_000, 1, 10_000_000, 100, { prefix: "$" }),
  field("monthlyDebts", "Other monthly debt", 1_000, 0, 1_000_000, 50, { prefix: "$" }),
  field("downPayment", "Down payment", 80_000, 0, 100_000_000, 1_000, { prefix: "$" }),
  field("interestRate", "Interest rate", 6.5, 0, 25, 0.01, { suffix: "%" }),
  field("termYears", "Loan term", 30, 1, 40, 1, { suffix: "years" }),
  field("propertyTaxRate", "Property tax", 1.1, 0, 10, 0.05, { suffix: "% / year" }),
  field("pmiRate", "PMI rate", 0.5, 0, 3, 0.05, { suffix: "% / year", helper: "Used below 20% down" }),
  field("homeInsurance", "Home insurance", 1_800, 0, 100_000, 100, { prefix: "$", suffix: "/ year" }),
  field("hoa", "HOA dues", 0, 0, 10_000, 25, { prefix: "$", suffix: "/ month" }),
  field("housingDti", "Housing DTI", 36, 1, 60, 0.5, { suffix: "% of income" }),
];

const mortgageRefinanceFields = [
  field("currentBalance", "Current balance", 300_000, 1, 100_000_000, 1_000, { prefix: "$" }),
  field("currentRate", "Current rate", 7, 0, 25, 0.01, { suffix: "%" }),
  field("remainingTermYears", "Remaining term", 25, 1, 40, 1, { suffix: "years" }),
  field("newRate", "New rate", 6, 0, 25, 0.01, { suffix: "%" }),
  field("newTermYears", "New loan term", 25, 1, 40, 1, { suffix: "years" }),
  field("closingCosts", "Closing costs", 6_000, 0, 1_000_000, 100, { prefix: "$" }),
];

const rentVsBuyFields = [
  field("monthlyRent", "Monthly rent", 2_500, 0, 1_000_000, 50, { prefix: "$" }),
  field("rentGrowth", "Annual rent growth", 3, 0, 20, 0.1, { suffix: "% / year" }),
  field("homePrice", "Home price", 450_000, 1, 100_000_000, 1_000, { prefix: "$" }),
  field("downPayment", "Down payment", 90_000, 0, 100_000_000, 1_000, { prefix: "$" }),
  field("interestRate", "Interest rate", 6.5, 0, 25, 0.01, { suffix: "%" }),
  field("termYears", "Loan term", 30, 1, 40, 1, { suffix: "years" }),
  field("propertyTaxRate", "Property tax", 1.1, 0, 10, 0.05, { suffix: "% / year" }),
  field("homeInsurance", "Home insurance", 1_800, 0, 100_000, 100, { prefix: "$", suffix: "/ year" }),
  field("maintenanceRate", "Maintenance", 1, 0, 10, 0.1, { suffix: "% of home / year" }),
  field("appreciationRate", "Home appreciation", 3, -20, 20, 0.1, { suffix: "% / year" }),
  field("sellingCostRate", "Selling costs", 6, 0, 15, 0.1, { suffix: "% of home value" }),
  field("yearsToCompare", "Time to compare", 7, 1, 60, 1, { suffix: "years" }),
];

const studentLoanPayoffFields = [
  field("balance", "Loan balance", 40_000, 0, 100_000_000, 100, { prefix: "$" }),
  field("annualRate", "Interest rate", 6.5, 0, 30, 0.01, { suffix: "%" }),
  field("monthlyPayment", "Monthly payment", 500, 0, 1_000_000, 10, { prefix: "$" }),
  field("extraPayment", "Extra each month", 100, 0, 1_000_000, 10, { prefix: "$", helper: "Optional additional payment" }),
];

const MAX_SCENARIO_YEARS = 100;

export const calculatorCatalog: CalculatorDefinition[] = [
  {
    slug: "etf-stock-overlap", category: "Plan", name: "ETF and stock overlap", title: "ETF and Stock Overlap Calculator",
    description: "Check exposure shared by one ETF and your direct stocks. Enter fund holding weights to calculate combined exposure, with clear partial-coverage limits.",
    seoTitle: "ETF and Stock Overlap Calculator — Equity Replay",
    seoDescription: "Calculate stock exposure through one ETF and direct holdings. Enter your weights to check overlapping positions, holdings coverage and unexamined exposure.",
    fields: [field("etfWeight", "ETF share of portfolio", 50, 0, 100, 0.01, { suffix: "%" }), field("stockInFund", "Stock weight inside ETF", 6, 0, 100, 0.01, { suffix: "%" }), field("directStock", "Direct stock share of portfolio", 10, 0, 100, 0.01, { suffix: "%" })],
    methodTitle: "How ETF and direct-stock exposure adds up",
    method: "Stock exposure through ETF (%) = ETF portfolio weight × stock weight inside ETF ÷ 100. Combined stock exposure (%) = direct portfolio weight + exposure through ETF. For example, 10% direct stock exposure plus a 6% holding inside an ETF that is 50% of your portfolio gives 13% combined exposure. Enter up to 20 distinct stock identifiers, combining duplicate positions. Inputs are rounded to two decimal places before calculation. Entered fund weights may total less than 100%, but never more. ETF allocation plus direct weights cannot exceed 100% of the portfolio. Unentered fund holdings and other portfolio positions remain explicitly unexamined. No totals are silently normalized.",
    disclaimer: "Educational calculation only, not financial advice. Equity Replay is a historical simulator, not a broker or a record of trades. This manual snapshot covers one ordinary, unleveraged equity ETF plus direct stocks. It is not a two-fund overlap comparison, complete look-through analysis, diversification score, correlation measure or trade recommendation. Cash, derivatives, leveraged and inverse exposures are not modeled. No fund holdings are fetched or verified.",
    faq: [
      { question: "What does this ETF overlap calculator measure?", answer: "It adds direct stock exposure to exposure through one ETF for the stock identifiers you enter. It reports shared stocks, not the percentage overlap between two ETFs." },
      { question: "Where do I get ETF holding weights?", answer: "Use the fund issuer's dated holdings list. Enter each stock's weight inside the ETF, not its weight in your whole portfolio. Use consistent portfolio valuation dates and record the holdings date. The tool does not verify the source or freshness." },
      { question: "Can I enter only the top holdings?", answer: "Yes, but the result is partial. Entered ETF holdings coverage is the sum of those fund weights. Unexamined ETF exposure is ETF portfolio weight × the remaining fund weight ÷ 100. Missing stocks may create additional overlap." },
      { question: "Does zero detected overlap mean I am diversified?", answer: "No. Omitted holdings, other funds, sectors and correlated exposures are not analyzed. Even a complete entered list does not prove diversification or verify the data." },
      { question: "Are edited inputs sent to the server?", answer: "Edits remain in page memory. Share calculation explicitly creates a URL fragment containing identifiers, weights and the holdings date. The fragment is not in the page HTTP request, but recipients and scripts can read it. Avoid sensitive inputs." },
    ],
    related: ["portfolio-concentration", "portfolio-drawdown", "investment-return"],
    guide: { blogSlug: "how-to-check-etf-overlap-with-your-stock-portfolio", title: "How to check ETF overlap with a stock portfolio" },
  },
  {
    slug: "portfolio-concentration", category: "Plan", name: "Portfolio concentration", title: "Portfolio Concentration Calculator",
    description: "Enter holding values to see portfolio weights and top-three concentration. Add your own target weights to check allocation drift.",
    seoTitle: "Portfolio Concentration & Allocation Drift Calculator — Equity Replay",
    seoDescription: "Calculate portfolio weights, largest-position and top-three concentration, then compare your current allocation with target weights in percentage points.",
    fields: exampleHoldings.map((holding, index) => field(`holding${index + 1}`, `${holding.name} current value`, holding.value, 0, 100_000_000, 0.01, { prefix: "$" })),
    methodTitle: "Portfolio weight and allocation drift formulas",
    method: "Holding weight (%) = current holding value ÷ total entered value × 100. Top-three concentration is the sum of the three largest weights, or all funded holdings if fewer than three are entered. Drift (percentage points) = current weight − your target weight. For example, $5,000 in a $10,000 portfolio is a 50% weight. Against a 40% target, drift is +10 percentage points, not a 10% investment return. Targets must total 100%; they are never silently normalized. Values and targets are rounded to two decimal places. Use current market values in one currency and combine duplicate positions before entering them.",
    disclaimer: "Educational calculation only, not financial advice. Equity Replay is a historical simulator, not a broker or a record of trades. This snapshot is not a risk score, a forecast, or an instruction to rebalance. ETFs are counted as positions; their underlying holdings, sector exposure, and correlations are not analyzed.",
    faq: [
      { question: "What does portfolio concentration measure here?", answer: "It measures how much of the entered portfolio value sits in its largest position and its three largest positions. It does not determine whether the portfolio is safe or suitably diversified." },
      { question: "How is portfolio drift calculated?", answer: "Subtract your target weight from the current weight. A 50% current weight against a 40% target is +10 percentage points. Negative drift means below your entered target. The tool does not choose targets or recommend trades." },
      { question: "Do I need target weights?", answer: "No. Concentration and current weights work without them. Enable target comparison only if you have your own targets; enter every target and make the total 100%." },
      { question: "Does this detect ETF overlap?", answer: "No. A fund is one entered position. Owning a fund and a direct stock can create underlying overlap that these totals cannot detect. Review fund holdings separately." },
      { question: "Can I enter cash or the same holding in several accounts?", answer: "Yes. Include cash if it belongs in the allocation you are measuring. Combine the same holding across accounts into one row. Use the same currency and valuation date for every row. The limit is 20 holdings; omitted positions change the denominator and results." },
    ],
    related: ["etf-stock-overlap", "portfolio-drawdown", "investment-return"],
    guide: { blogSlug: "what-is-portfolio-rebalancing", title: "Understand allocation drift and portfolio rebalancing" },
  },
  {
    slug: "portfolio-drawdown", category: "Plan", name: "Portfolio drawdown", title: "Portfolio Drawdown Calculator",
    description: "Compare a portfolio's previous peak with its current value. See the loss and the percentage gain needed to recover it.",
    seoTitle: "Portfolio Drawdown & Loss Recovery Calculator — Equity Replay",
    seoDescription: "Calculate portfolio drawdown, dollar loss, and the gain needed to recover from a loss. Enter peak and current values to see the formulas and a worked example.",
    fields: [
      field("peak", "Previous peak value", 10_000, 0.01, 100_000_000, 0.01, { prefix: "$", helper: "Use a positive peak from the period you want to measure." }),
      field("current", "Current portfolio value", 8_000, 0, 100_000_000, 0.01, { prefix: "$", helper: "Use the same currency and valuation basis as the peak. Amounts are rounded to cents." }),
    ],
    methodTitle: "Portfolio drawdown and loss-recovery formulas",
    method: "Drawdown (%) = max(0, peak − current) ÷ peak × 100. Recovery gain (%) = (peak − current) ÷ current × 100 when current is below peak and greater than zero. For example, a $10,000 peak falling to $8,000 means a $2,000 loss and 20% drawdown. Regaining $10,000 requires a 25% gain on $8,000. At or above peak, no recovery is needed; at zero, no finite percentage gain can restore the balance.",
    disclaimer: "Educational calculation only, not financial advice. Equity Replay is a historical simulator, not a broker or a record of trades. These two values cannot establish maximum drawdown or predict recovery time. Deposits, withdrawals, dividends, fees, and taxes can change the comparison; use consistent values.",
    faq: [
      { question: "Why does a 20% loss need a 25% gain to recover?", answer: "The loss is measured against the larger peak. Recovery is measured against the smaller remaining balance: $2,000 divided by $8,000 is 25%, not 20%. A 50% loss requires a 100% gain." },
      { question: "Is this a maximum drawdown calculator?", answer: "No. This calculates the decline from one entered peak to one current value. Maximum drawdown is the deepest peak-to-trough decline over a dated series and requires checking all earlier peaks and subsequent troughs." },
      { question: "How long will recovery take?", answer: "These inputs cannot tell you. The required gain is arithmetic, not a forecast, and recovery is not guaranteed." },
      { question: "What if the current value is zero or above the peak?", answer: "Zero means a 100% loss and no finite recovery percentage. At or above the entered peak, drawdown and the recovery gain are zero. If the current value is a new high, use it as the peak for later measurements." },
      { question: "Can I include deposits or withdrawals?", answer: "Not in a direct two-value comparison. A withdrawal can look like a loss and a deposit can hide one. Use cash-flow-adjusted, consistently valued figures before interpreting the result as investment performance." },
    ],
    related: ["portfolio-concentration", "investment-return", "investment-fees"],
    guide: { blogSlug: "how-to-calculate-portfolio-drawdown", title: "How to calculate portfolio drawdown: examples and maximum drawdown" },
  },
  {
    slug: "compound-interest",
    category: "Grow",
    name: "Compound interest",
    title: "Compound interest calculator",
    description: "See how an opening balance, regular contributions, and time can shape a future balance.",
    seoTitle: "Compound Interest Calculator — See Growth Over Time | Equity Replay",
    seoDescription: "Use a simple compound interest calculator to estimate how a starting balance and monthly contributions could grow over time.",
    fields: compoundInterestFields,
    methodTitle: "Time is the quiet variable.",
    method: "This estimate compounds monthly and treats each monthly contribution as arriving at the end of the month. It is a planning illustration, not a projection of any specific investment.",
    disclaimer: "The rate is an assumption you can change. Real returns are uneven, and this calculation does not model taxes, fees, or market volatility.",
    faq: [
      { question: "What does compound interest mean?", answer: "Compound interest is growth earned on both the original balance and earlier growth. Contributions and the time horizon can materially change the result." },
      { question: "Are monthly contributions included?", answer: "Yes. The calculator adds the monthly contribution at the end of each month and compounds the balance monthly." },
    ],
    related: ["investment-fees", "inflation", "savings-goal"],
  },
  {
    slug: "inflation",
    category: "Plan",
    name: "Inflation",
    title: "Inflation calculator",
    description: "Translate today’s price into a future cost—or see what today’s money may buy later.",
    seoTitle: "Inflation Calculator — See Future Buying Power | Equity Replay",
    seoDescription: "Use an inflation calculator to estimate how a future price and the buying power of today’s money may change over a time period you choose.",
    fields: inflationFields,
    methodTitle: "A dollar has a timeline.",
    method: "The calculator applies the same annual inflation assumption once per year. It is a way to make purchasing-power changes easier to see, not a forecast of future prices.",
    disclaimer: "Inflation differs by category, location, and time. Use the rate as a scenario rather than a promise about future prices.",
    faq: [
      { question: "How does the inflation calculation work?", answer: "The future cost is the amount today multiplied by one plus the annual inflation rate, raised to the number of years." },
      { question: "Does this predict the price of a specific item?", answer: "No. It applies one broad annual rate. A specific item can rise faster or slower than the assumption." },
    ],
    related: ["compound-interest", "savings-goal", "mortgage-payment"],
  },
  {
    slug: "savings-goal",
    category: "Plan",
    name: "Savings goal",
    title: "Savings goal calculator",
    description: "Start with a target, add what you already have, and find the monthly pace that closes the gap.",
    seoTitle: "Savings Goal Calculator — Find Your Monthly Target | Equity Replay",
    seoDescription: "Calculate the monthly amount needed to reach a savings goal using your starting balance, time horizon, and assumed rate.",
    fields: savingsGoalFields,
    methodTitle: "A target becomes a rhythm.",
    method: "The estimate grows the current balance monthly, then solves for the equal monthly contribution needed to reach the target by the end of the selected horizon.",
    disclaimer: "The result is a mathematical estimate. It does not recommend a savings product or account, and actual rates can change.",
    faq: [
      { question: "What if I have already saved enough?", answer: "If the current balance would reach the target under the selected assumption, the required monthly contribution is shown as $0." },
      { question: "Are contributions made at the beginning or end of the month?", answer: "This calculator assumes contributions are made at the end of each month." },
    ],
    related: ["compound-interest", "inflation", "investment-fees"],
  },
  {
    slug: "investment-fees",
    category: "Grow",
    name: "Investment fees",
    title: "Investment fee calculator",
    description: "Put a balance, contributions, return assumption, and annual fee on the same page to see the difference.",
    seoTitle: "Investment Fee Calculator — See the Cost of Annual Fees | Equity Replay",
    seoDescription: "Use an investment fee calculator to estimate how annual fees affect an illustrative ending balance from your balance, contributions, return, and fee.",
    fields: investmentFeeFields,
    methodTitle: "Small percentages compound too.",
    method: "The calculator compares two monthly-compounded scenarios: one with the assumed return and one with the assumed return reduced by the annual fee.",
    disclaimer: "This is a fee-drag illustration, not an investment recommendation. It excludes taxes, trading costs, and changes in returns.",
    faq: [
      { question: "What does the fee represent?", answer: "It represents an annual percentage deducted from the return assumption for this illustration. Actual fees can be charged in different ways." },
      { question: "Does this show a guaranteed loss?", answer: "No. It shows the difference between two assumptions. Real results vary with balances, contributions, fees, and returns." },
    ],
    related: ["compound-interest", "investment-return", "retirement-401k"],
  },
  {
    slug: "mortgage-payment",
    category: "Borrow",
    name: "Mortgage payment",
    title: "Mortgage payment calculator",
    description: "Estimate principal, interest, taxes, insurance, PMI, and other monthly housing costs before comparing a loan offer.",
    seoTitle: "Mortgage Payment Calculator — Estimate Monthly Cost | Equity Replay",
    seoDescription: "Estimate a mortgage’s principal and interest payment, private mortgage insurance, property tax, insurance, and HOA costs.",
    fields: mortgageFields,
    methodTitle: "The payment is more than principal and interest.",
    method: "The estimate uses a standard fixed-rate amortization formula for principal and interest. Property tax, insurance, HOA, and a simplified PMI estimate are shown separately so the total monthly housing cost is easier to read.",
    disclaimer: "This is an estimate, not a loan offer. PMI is modeled as an annual rate on the original loan amount when the down payment is below 20%; actual loan rules, cancellation timing, FHA insurance, closing costs, maintenance, and prepaid escrow are not modeled.",
    faq: [
      { question: "Does the mortgage payment include taxes, insurance, and PMI?", answer: "Yes, if you enter them. The result separates principal and interest from property tax, home insurance, HOA dues, and an estimated PMI amount when the down payment is below 20%." },
      { question: "Does this calculate affordability?", answer: "No. It estimates a payment for the inputs you provide. It does not assess income, credit, underwriting, or whether a home is affordable for you." },
    ],
    related: ["private-mortgage-insurance", "mortgage-affordability", "mortgage-refinance"],
  },
  {
    slug: "private-mortgage-insurance",
    category: "Borrow",
    name: "Private mortgage insurance",
    title: "Private mortgage insurance calculator",
    description: "Estimate monthly PMI for a conventional mortgage with this illustrative formula: original loan amount × annual PMI rate ÷ 12 when the down payment is below 20%.",
    seoTitle: "Private Mortgage Insurance Calculator — Estimate PMI | Equity Replay",
    seoDescription: "Use a private mortgage insurance calculator to estimate monthly PMI from home price, down payment, loan amount, and annual PMI rate for a conventional loan.",
    fields: privateMortgageInsuranceFields,
    methodTitle: "The down payment changes the monthly cost.",
    method: "The estimate applies an annual PMI rate to the original loan amount when the down payment is below 20% of the home price. The chart shows how the monthly estimate changes at several down-payment levels.",
    disclaimer: "This is an educational estimate, not a lender quote. PMI rates vary by loan type, credit profile, lender, and other underwriting factors. The calculation does not model FHA mortgage insurance, upfront premiums, amortization, or cancellation schedules.",
    faq: [
      { question: "When does PMI usually apply?", answer: "For many conventional loans, PMI may be required when the down payment is below 20% of the home price. Loan rules and lender requirements vary." },
      { question: "How much does private mortgage insurance cost?", answer: "There is no single PMI price. This illustration multiplies the original loan amount by the annual PMI rate and divides by 12. Actual premiums vary by lender, loan type, credit profile, and loan-to-value ratio." },
      { question: "How is PMI calculated?", answer: "The calculator multiplies the original loan amount by the annual PMI rate and divides by 12. It shows $0 when the down payment is 20% or more." },
      { question: "Does PMI automatically end?", answer: "This calculator does not model cancellation or amortization. Ask the lender or servicer about the cancellation rules for the loan you are considering." },
    ],
    related: ["mortgage-payment", "mortgage-affordability", "mortgage-refinance"],
  },
  {
    slug: "car-loan-payment",
    category: "Borrow",
    name: "Car loan payment",
    title: "Car loan payment calculator",
    description: "See the monthly payment, amount financed, and total interest—not just the sticker price.",
    seoTitle: "Car Loan Payment Calculator — Monthly Payment & Interest | Equity Replay",
    seoDescription: "Use a car loan payment calculator to estimate amount financed, monthly payment, total interest, and total payments after tax, fees, and your down payment.",
    fields: carLoanFields,
    methodTitle: "Price the loan, not just the car.",
    method: "The estimate adds sales tax and fees, subtracts the down payment and trade-in value, then applies a standard fixed-rate loan payment formula.",
    disclaimer: "This is an estimate, not a lender quote. Dealer fees, registration, rebates, negative equity, and lender-specific terms can change the final numbers.",
    faq: [
      { question: "What is included in the amount financed?", answer: "The estimate includes the vehicle price, sales tax, and fees, then subtracts the down payment and trade-in value." },
      { question: "Is APR the same as the interest rate?", answer: "APR can include the interest rate and certain required costs. Use the APR shown in the lender’s disclosure when comparing offers." },
    ],
    related: ["mortgage-payment", "savings-goal", "inflation"],
  },
  {
    slug: "investment-return",
    category: "Grow",
    name: "Investment return",
    title: "Investment return calculator",
    description: "Turn a starting value and ending value into total return and an annualized CAGR scenario.",
    seoTitle: "Investment Return Calculator — Calculate CAGR | Equity Replay",
    seoDescription: "Calculate total investment return and compound annual growth rate (CAGR) from a starting value, ending value, and time horizon.",
    fields: investmentReturnFields,
    methodTitle: "One result, two ways to read it.",
    method: "The total return compares the ending value with the starting value. CAGR annualizes that change across the selected number of years as if growth had compounded at one steady rate.",
    disclaimer: "This is a historical or hypothetical return illustration. It does not account for deposits, withdrawals, taxes, fees, dividends, or the uneven path real markets take.",
    faq: [
      { question: "What is CAGR?", answer: "CAGR is the compound annual growth rate: the single annualized rate that would turn the starting value into the ending value over the selected period." },
      { question: "Is CAGR the same as an actual yearly return?", answer: "No. CAGR smooths the path into one annualized number. It does not show the volatility or sequence of returns between the two values." },
    ],
    related: ["compound-interest", "investment-fees", "savings-goal"],
  },
  {
    slug: "credit-card-payoff",
    category: "Borrow",
    name: "Credit card payoff",
    title: "Credit card payoff calculator",
    description: "See how a monthly payment and an extra amount could change the time and interest in a fixed payoff scenario.",
    seoTitle: "Credit Card Payoff Calculator — Estimate Interest & Time | Equity Replay",
    seoDescription: "Use a credit card payoff calculator to estimate payoff time, total interest, and the effect of an extra monthly payment.",
    fields: creditCardPayoffFields,
    methodTitle: "The payment changes the calendar.",
    method: "The estimate applies the APR monthly, then subtracts the fixed monthly payment until the balance reaches zero. The optional extra payment is added to that monthly amount.",
    disclaimer: "This is a fixed-payment illustration, not debt advice. It does not model new charges, promotional APRs, late fees, minimum-payment formulas, or issuer-specific rules.",
    faq: [
      { question: "What payment is used?", answer: "The calculator adds the monthly payment and the optional extra amount, then uses that combined amount for the payoff scenario." },
      { question: "What if the payment does not cover interest?", answer: "The calculator flags the scenario as not reaching payoff because the payment is not reducing the balance under the selected APR." },
    ],
    related: ["debt-payoff", "student-loan-payoff", "savings-goal"],
  },
  {
    slug: "debt-payoff",
    category: "Borrow",
    name: "Debt payoff",
    title: "Debt payoff calculator",
    description: "Enter up to three balances and compare illustrative avalanche and snowball payoff paths.",
    seoTitle: "Debt Payoff Calculator — Compare Repayment Scenarios | Equity Replay",
    seoDescription: "Compare illustrative debt payoff timelines and interest using up to three balances, APRs, minimum payments, and an extra monthly amount.",
    fields: debtPayoffFields,
    methodTitle: "A payoff plan is a set of assumptions.",
    method: "The calculator applies each debt’s monthly interest, pays the entered minimums, and directs the remaining monthly budget to either the highest-rate balance (avalanche) or the smallest balance (snowball).",
    disclaimer: "This is an educational comparison, not personalized debt advice. It assumes no new charges, stable rates, fixed minimum payments, and no fees or lender-specific program rules.",
    faq: [
      { question: "What is the avalanche method?", answer: "In this illustration, the extra payment goes toward the active balance with the highest APR after minimum payments are applied." },
      { question: "What is the snowball method?", answer: "In this illustration, the extra payment goes toward the smallest active balance after minimum payments are applied." },
      { question: "Can I compare more than three debts?", answer: "This first version supports up to three balances. Combine or leave out smaller balances for a rough scenario, or use the single-debt credit card and student loan calculators for focused cases." },
    ],
    related: ["credit-card-payoff", "student-loan-payoff", "savings-goal"],
  },
  {
    slug: "retirement-401k",
    category: "Grow",
    name: "401(k) and retirement",
    title: "401(k) and retirement calculator",
    description: "Explore how contributions, an employer match, salary growth, and time could shape a retirement balance.",
    seoTitle: "401(k) Calculator — Estimate Retirement Savings | Equity Replay",
    seoDescription: "Estimate an illustrative 401(k) or retirement balance from salary, contributions, employer match, growth, return, and retirement age.",
    fields: retirementFields,
    methodTitle: "Time turns a contribution into a range.",
    method: "The estimate compounds the current balance annually, adds an employee contribution and an employer match based on salary, then increases salary by the selected growth assumption each year.",
    disclaimer: "This is an educational scenario, not a retirement recommendation or tax projection. It does not model contribution limits, vesting, taxes, withdrawals, inflation, or plan-specific rules.",
    faq: [
      { question: "How is the employer match modeled?", answer: "The employer match is applied as a percentage of your contribution. For example, 50% means the employer contribution equals half of your contribution in this illustration." },
      { question: "Does this include taxes and inflation?", answer: "No. The result is a nominal balance estimate and does not model taxes, purchasing power, withdrawals, or plan rules." },
    ],
    related: ["compound-interest", "investment-fees", "inflation"],
  },
  {
    slug: "net-worth",
    category: "Plan",
    name: "Net worth",
    title: "Net worth calculator",
    description: "List the assets and debts that make up your balance sheet, then see the difference in one view.",
    seoTitle: "Net Worth Calculator — Add Assets and Debts | Equity Replay",
    seoDescription: "Calculate an illustrative net worth by adding cash, investments, property, vehicles, and retirement accounts, then subtracting debts and other liabilities.",
    fields: netWorthFields,
    methodTitle: "A snapshot can make a direction visible.",
    method: "Net worth is calculated as total assets minus total liabilities. Liquid assets are shown separately as cash and non-retirement investments so the composition is easier to inspect.",
    disclaimer: "This is a personal balance-sheet snapshot, not a valuation or financial plan. Use current estimates consistently, especially for property, vehicles, and other assets.",
    faq: [
      { question: "What is net worth?", answer: "Net worth is the value of what you own minus what you owe. Assets can include cash, investments, property, vehicles, and retirement accounts; liabilities can include loans and credit card balances." },
      { question: "Should retirement accounts be included?", answer: "You can include them as an asset. The calculator does not estimate taxes or early-withdrawal costs, so treat the result as a gross snapshot." },
    ],
    related: ["emergency-fund", "savings-goal", "investment-return"],
  },
  {
    slug: "emergency-fund",
    category: "Plan",
    name: "Emergency fund",
    title: "Emergency fund calculator",
    description: "Set a reserve target from essential monthly expenses, current savings, and the pace you can add.",
    seoTitle: "Emergency Fund Calculator — Set a Savings Target | Equity Replay",
    seoDescription: "Calculate an illustrative emergency fund target, savings shortfall, and months to target from essential expenses and monthly contributions.",
    fields: emergencyFundFields,
    methodTitle: "A reserve starts with a number you can name.",
    method: "The target multiplies essential monthly expenses by the number of months you choose. The estimated time to target divides the remaining gap by the monthly contribution, without assuming investment growth.",
    disclaimer: "The target months are your chosen scenario, not a universal rule. This calculator does not assess insurance, income stability, dependents, or individual circumstances.",
    faq: [
      { question: "How large should an emergency fund be?", answer: "There is no single amount for everyone. Choose a target-month assumption that reflects your situation, then change it to compare scenarios." },
      { question: "Does the calculator assume interest?", answer: "No. It uses essential expenses, current savings, and monthly contributions only, so the result stays easy to inspect." },
    ],
    related: ["savings-goal", "inflation", "net-worth"],
  },
  {
    slug: "mortgage-affordability",
    category: "Borrow",
    name: "Mortgage affordability",
    title: "Mortgage affordability calculator",
    description: "Translate income, existing debt, a down payment, and housing costs into an illustrative price range.",
    seoTitle: "Mortgage Affordability Calculator — Estimate Home Price | Equity Replay",
    seoDescription: "Estimate an illustrative home price from income, monthly debt, down payment, mortgage rate, taxes, insurance, HOA, and housing DTI.",
    fields: mortgageAffordabilityFields,
    methodTitle: "Affordability is a scenario, not a verdict.",
    method: "The calculator reserves the selected share of gross monthly income for housing after subtracting other monthly debt, then solves for the highest home price whose modeled payment fits that budget.",
    disclaimer: "This is not lender underwriting or a recommendation to borrow. It does not model credit, closing costs, income taxes, utilities, maintenance, reserves, loan limits, or lender-specific DTI rules.",
    faq: [
      { question: "What does housing DTI mean here?", answer: "It is the share of gross monthly income reserved for the modeled housing cost in this scenario. The calculator subtracts other monthly debt before solving for a price." },
      { question: "Does the result mean I qualify for that home price?", answer: "No. Qualification depends on credit, documentation, loan program, reserves, lender rules, and costs this illustration does not model." },
    ],
    related: ["mortgage-payment", "private-mortgage-insurance", "mortgage-refinance"],
  },
  {
    slug: "mortgage-refinance",
    category: "Borrow",
    name: "Mortgage refinance",
    title: "Mortgage refinance calculator",
    description: "Compare current and new mortgage payments to estimate a simple monthly-savings break-even point.",
    seoTitle: "Mortgage Refinance Calculator — Find the Break-Even Point | Equity Replay",
    seoDescription: "Estimate mortgage refinance savings, interest difference, and the months needed to recover closing costs using current and new loan assumptions.",
    fields: mortgageRefinanceFields,
    methodTitle: "The break-even date is the first question.",
    method: "The calculator compares standard fixed-rate principal-and-interest payments, subtracts the new payment from the current payment, and divides closing costs by monthly savings when savings are positive.",
    disclaimer: "This is a simplified comparison, not a refinance recommendation or lender quote. It excludes taxes, insurance, points, prepayment penalties, cash-out changes, and loan-specific fees beyond the closing-cost input.",
    faq: [
      { question: "How is the refinance break-even point calculated?", answer: "Closing costs are divided by the estimated monthly payment savings. If the new payment is not lower, the calculator shows that a monthly-savings break-even is not reached." },
      { question: "Does a lower payment always mean a better refinance?", answer: "No. A longer new term can lower the payment while increasing the total time in debt. Compare the term and interest scenarios as well as the monthly payment." },
    ],
    related: ["mortgage-payment", "mortgage-affordability", "rent-vs-buy"],
  },
  {
    slug: "rent-vs-buy",
    category: "Plan",
    name: "Rent vs. buy",
    title: "Rent vs. buy calculator",
    description: "Compare rent paid with a simplified net cost of buying across a time horizon you choose.",
    seoTitle: "Rent vs. Buy Calculator — Compare Housing Scenarios | Equity Replay",
    seoDescription: "Compare illustrative renting and buying costs using rent growth, home price, mortgage, maintenance, appreciation, and selling costs.",
    fields: rentVsBuyFields,
    methodTitle: "The horizon changes the story.",
    method: "The estimate grows rent annually, adds the modeled cash costs of owning, subtracts remaining home equity, and includes selling costs at the end of the selected horizon.",
    disclaimer: "This is a simplified scenario comparison, not a housing recommendation. It excludes closing costs, utilities, tax deductions, financing opportunity cost, repairs beyond the maintenance assumption, and local market differences.",
    faq: [
      { question: "What does buying cost include?", answer: "The illustration includes the down payment, mortgage principal and interest, property tax, insurance, maintenance, and selling costs, then subtracts estimated remaining home equity." },
      { question: "Does this tell me whether I should rent or buy?", answer: "No. It makes selected assumptions visible so you can compare scenarios. Lifestyle, mobility, liquidity, taxes, and local conditions are not fully captured." },
    ],
    related: ["mortgage-payment", "mortgage-affordability", "inflation"],
  },
  {
    slug: "student-loan-payoff",
    category: "Borrow",
    name: "Student loan payoff",
    title: "Student loan payoff calculator",
    description: "Estimate how a fixed payment and an optional extra amount could change payoff time and interest.",
    seoTitle: "Student Loan Payoff Calculator — Estimate Time & Interest | Equity Replay",
    seoDescription: "Use a student loan payoff calculator to estimate payoff time, total interest, and the effect of an extra monthly payment.",
    fields: studentLoanPayoffFields,
    methodTitle: "A fixed payment reveals the shape of the loan.",
    method: "The estimate applies the selected interest rate monthly and subtracts the fixed payment plus any extra amount until the balance reaches zero.",
    disclaimer: "This is a fixed-rate payoff illustration, not federal student-loan guidance. It does not model income-driven plans, forgiveness, capitalization, deferment, subsidies, fees, or servicer rules.",
    faq: [
      { question: "Does this model federal repayment plans?", answer: "No. It models a fixed-rate balance with a fixed monthly payment. Federal and private loan terms can work differently." },
      { question: "What does the extra payment do?", answer: "The extra amount is added to the monthly payment in the scenario. The result shows how that changes the estimated payoff period and interest." },
    ],
    related: ["debt-payoff", "credit-card-payoff", "savings-goal"],
  },
];

const calculatorBySlug = new Map(calculatorCatalog.map((calculator) => [calculator.slug, calculator]));

export const featuredCalculatorSlugs: CalculatorSlug[] = [
  "investment-return",
  "credit-card-payoff",
  "debt-payoff",
  "retirement-401k",
  "net-worth",
];

export function findCalculator(slug?: string | null) {
  return slug ? calculatorBySlug.get(slug as CalculatorSlug) : undefined;
}

export function defaultCalculatorValues(definition: CalculatorDefinition) {
  return Object.fromEntries(definition.fields.map((item) => [item.key, item.defaultValue]));
}

export function clampCalculatorValue(fieldDefinition: CalculatorField, value: number) {
  if (!Number.isFinite(value)) return fieldDefinition.defaultValue;
  return Math.min(fieldDefinition.max, Math.max(fieldDefinition.min, value));
}

function monthlyFutureValue(principal: number, monthlyContribution: number, monthlyRate: number, months: number) {
  const growth = Math.pow(1 + monthlyRate, months);
  if (monthlyRate === 0) return principal + monthlyContribution * months;
  return principal * growth + monthlyContribution * ((growth - 1) / monthlyRate);
}

function monthlyLoanPayment(principal: number, annualRate: number, months: number) {
  if (principal <= 0 || months <= 0) return 0;
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate === 0) return principal / months;
  const growth = Math.pow(1 + monthlyRate, months);
  return principal * ((monthlyRate * growth) / (growth - 1));
}

function projectionPoints(years: number, calculate: (year: number) => number) {
  const count = Math.min(7, Math.max(2, Math.round(years) + 1));
  const yearsToShow = [...new Set(Array.from({ length: count }, (_, index) => Math.round((index * years) / (count - 1))))];
  return yearsToShow.map((year) => ({ label: year === 0 ? "Now" : `${year}y`, value: Math.max(0, calculate(year)) }));
}

function monthProjectionPoints(months: number, calculate: (month: number) => number) {
  const safeMonths = Math.max(1, Math.round(months));
  const count = Math.min(7, Math.max(2, safeMonths + 1));
  const monthsToShow = [...new Set(Array.from({ length: count }, (_, index) => Math.round((index * safeMonths) / (count - 1))))];
  return monthsToShow.map((month) => ({ label: month === 0 ? "Now" : `${month}m`, value: Math.max(0, calculate(month)) }));
}

function displayCurrency(value: number) {
  if (!Number.isFinite(value)) return "$0";
  return value < 0 ? `−${currency(Math.abs(value))}` : currency(value);
}

function displayMonths(months: number) {
  if (!Number.isFinite(months)) return "Not reached";
  const safeMonths = Math.max(0, Math.ceil(months));
  if (safeMonths >= 12) {
    const years = Math.floor(safeMonths / 12);
    const remainder = safeMonths % 12;
    return remainder === 0 ? `${years} year${years === 1 ? "" : "s"}` : `${years}y ${remainder}m`;
  }
  return `${safeMonths} month${safeMonths === 1 ? "" : "s"}`;
}

type FixedPayoffSchedule = {
  months: number;
  totalInterest: number;
  totalPaid: number;
  history: number[];
};

function fixedPayoffSchedule(balance: number, annualRate: number, payment: number, maxMonths = 1_200): FixedPayoffSchedule {
  let remaining = Math.max(0, Number.isFinite(balance) ? balance : 0);
  const monthlyRate = Math.max(0, Number.isFinite(annualRate) ? annualRate : 0) / 100 / 12;
  const history = [remaining];
  let totalInterest = 0;
  let totalPaid = 0;
  let months = 0;
  const safePayment = Math.max(0, Number.isFinite(payment) ? payment : 0);
  while (remaining > 0.005 && months < maxMonths) {
    const interest = remaining * monthlyRate;
    if (safePayment <= interest && remaining > 0) return { months: Number.POSITIVE_INFINITY, totalInterest, totalPaid, history };
    const applied = Math.min(remaining + interest, safePayment);
    remaining = Math.max(0, remaining + interest - applied);
    totalInterest += interest;
    totalPaid += applied;
    months += 1;
    history.push(remaining);
  }
  if (remaining > 0.005) return { months: Number.POSITIVE_INFINITY, totalInterest, totalPaid, history };
  history[history.length - 1] = 0;
  return { months, totalInterest, totalPaid, history };
}

function payoffChart(schedule: FixedPayoffSchedule, valueLabel: string) {
  const lastMonth = Math.max(1, schedule.history.length - 1);
  return lineChart(monthProjectionPoints(lastMonth, (month) => {
    const index = Math.min(schedule.history.length - 1, Math.round(month));
    return schedule.history[index] ?? 0;
  }), valueLabel);
}

type DebtInput = { balance: number; annualRate: number; minimumPayment: number };
type DebtPayoffScenario = FixedPayoffSchedule;

function debtPayoffSchedule(inputDebts: DebtInput[], extraPayment: number, method: "avalanche" | "snowball", maxMonths = 1_200): DebtPayoffScenario {
  const debts = inputDebts.map((debt) => ({
    balance: Math.max(0, Number.isFinite(debt.balance) ? debt.balance : 0),
    annualRate: Math.max(0, Number.isFinite(debt.annualRate) ? debt.annualRate : 0),
    minimumPayment: Math.max(0, Number.isFinite(debt.minimumPayment) ? debt.minimumPayment : 0),
  }));
  const monthlyBudget = debts.reduce((sum, debt) => sum + debt.minimumPayment, 0) + Math.max(0, Number.isFinite(extraPayment) ? extraPayment : 0);
  const history = [debts.reduce((sum, debt) => sum + debt.balance, 0)];
  let totalInterest = 0;
  let totalPaid = 0;
  let months = 0;
  if (history[0] === 0) return { months: 0, totalInterest: 0, totalPaid: 0, history };
  if (monthlyBudget <= 0) return { months: Number.POSITIVE_INFINITY, totalInterest: 0, totalPaid: 0, history };

  while (history[history.length - 1] > 0.005 && months < maxMonths) {
    debts.forEach((debt) => {
      if (debt.balance <= 0) return;
      const interest = debt.balance * (debt.annualRate / 100 / 12);
      debt.balance += interest;
      totalInterest += interest;
    });
    let budget = monthlyBudget;
    debts.forEach((debt) => {
      if (debt.balance <= 0) return;
      const minimum = Math.min(debt.balance, debt.minimumPayment);
      debt.balance -= minimum;
      budget -= minimum;
      totalPaid += minimum;
    });
    while (budget > 0.005) {
      const active = debts.filter((debt) => debt.balance > 0.005);
      if (active.length === 0) break;
      active.sort((left, right) => method === "avalanche"
        ? right.annualRate - left.annualRate || left.balance - right.balance
        : left.balance - right.balance || right.annualRate - left.annualRate);
      const target = active[0];
      const applied = Math.min(target.balance, budget);
      target.balance -= applied;
      budget -= applied;
      totalPaid += applied;
    }
    months += 1;
    history.push(Math.max(0, debts.reduce((sum, debt) => sum + debt.balance, 0)));
  }
  if (history[history.length - 1] > 0.005) return { months: Number.POSITIVE_INFINITY, totalInterest, totalPaid, history };
  history[history.length - 1] = 0;
  return { months, totalInterest, totalPaid, history };
}

function remainingLoanBalance(principal: number, annualRate: number, totalMonths: number, elapsedMonths: number) {
  const safePrincipal = Math.max(0, principal);
  const safeMonths = Math.max(1, Math.round(totalMonths));
  const elapsed = Math.min(safeMonths, Math.max(0, Math.round(elapsedMonths)));
  if (safePrincipal === 0 || elapsed >= safeMonths) return 0;
  const monthlyRate = Math.max(0, annualRate) / 100 / 12;
  if (monthlyRate === 0) return Math.max(0, safePrincipal * (1 - elapsed / safeMonths));
  const growth = Math.pow(1 + monthlyRate, elapsed);
  const payment = monthlyLoanPayment(safePrincipal, annualRate, safeMonths);
  return Math.max(0, safePrincipal * growth - payment * ((growth - 1) / monthlyRate));
}

function lineChart(points: Array<{ label: string; value: number }>, valueLabel: string): CalculatorChart {
  return { kind: "line", points, valueLabel };
}

function estimateMortgageInsurance(homePrice: number, downPayment: number, annualRate: number) {
  const safeHomePrice = Math.max(0, Number.isFinite(homePrice) ? homePrice : 0);
  const safeDownPayment = Math.min(safeHomePrice, Math.max(0, Number.isFinite(downPayment) ? downPayment : 0));
  const loanAmount = Math.max(0, safeHomePrice - safeDownPayment);
  const downPaymentPercent = safeHomePrice > 0 ? (safeDownPayment / safeHomePrice) * 100 : 0;
  const applies = loanAmount > 0 && downPaymentPercent < 20;
  const safeAnnualRate = Math.max(0, Number.isFinite(annualRate) ? annualRate : 0);
  const annual = applies ? loanAmount * (safeAnnualRate / 100) : 0;
  return { loanAmount, downPayment: safeDownPayment, downPaymentPercent, applies, annual, monthly: annual / 12 };
}

function breakdownChart(segments: Array<{ label: string; value: number; tone: "accent" | "blue" | "orange" | "purple" | "negative" }>, valueLabel: string): CalculatorChart {
  return { kind: "breakdown", segments, valueLabel };
}

function calculateCompoundInterest(values: Record<string, number>): CalculatorResult {
  const initial = values.initial;
  const monthlyContribution = values.monthlyContribution;
  const annualRate = values.annualRate;
  const years = values.years;
  const months = years * 12;
  const totalContributions = initial + monthlyContribution * months;
  const ending = monthlyFutureValue(initial, monthlyContribution, annualRate / 100 / 12, months);
  const interest = ending - totalContributions;
  return {
    headlineLabel: "Illustrative future value",
    headlineValue: currency(ending),
    headlineDetail: `after ${years} years at ${annualRate.toFixed(1)}% annually`,
    headlineTone: "positive",
    stats: [
      { label: "Total contributions", value: currency(totalContributions) },
      { label: "Growth from interest", value: currency(interest), tone: "positive" },
      { label: "Monthly contribution", value: currency(monthlyContribution, true) },
    ],
    chart: lineChart(projectionPoints(years, (year) => monthlyFutureValue(initial, monthlyContribution, annualRate / 100 / 12, year * 12)), "Balance"),
    note: "Monthly compounding · contributions added at month end",
  };
}

function calculateInflation(values: Record<string, number>): CalculatorResult {
  const amount = values.amount;
  const annualRate = values.inflationRate;
  const years = values.years;
  const futureCost = amount * Math.pow(1 + annualRate / 100, years);
  const buyingPower = amount / Math.pow(1 + annualRate / 100, years);
  return {
    headlineLabel: "Future cost of today’s amount",
    headlineValue: currency(futureCost),
    headlineDetail: `after ${years} years at ${annualRate.toFixed(1)}% inflation`,
    stats: [
      { label: "Buying power of today’s amount", value: currency(buyingPower) },
      { label: "Price increase", value: percentage((futureCost / Math.max(1, amount) - 1) * 100), tone: "negative" },
      { label: "Amount today", value: currency(amount) },
    ],
    chart: lineChart(projectionPoints(years, (year) => amount * Math.pow(1 + annualRate / 100, year)), "Future cost"),
    note: "One annual inflation assumption · not a category-specific forecast",
  };
}

function calculateSavingsGoal(values: Record<string, number>): CalculatorResult {
  const target = values.target;
  const current = values.current;
  const annualRate = values.annualRate;
  const years = values.years;
  const months = years * 12;
  const monthlyRate = annualRate / 100 / 12;
  const currentAtEnd = monthlyFutureValue(current, 0, monthlyRate, months);
  const gap = Math.max(0, target - currentAtEnd);
  const growthFactor = Math.pow(1 + monthlyRate, months);
  const monthlyContribution = gap === 0 ? 0 : monthlyRate === 0 ? gap / months : gap * monthlyRate / (growthFactor - 1);
  const totalAdded = monthlyContribution * months;
  const interest = Math.max(0, target - current - totalAdded);
  return {
    headlineLabel: "Monthly amount to set aside",
    headlineValue: currency(monthlyContribution, true),
    headlineDetail: monthlyContribution === 0 ? "Your current balance reaches the target under this assumption" : `to reach ${currency(target)} in ${years} years`,
    headlineTone: "positive",
    stats: [
      { label: "Total monthly contributions", value: currency(totalAdded) },
      { label: "Growth included", value: currency(interest), tone: "positive" },
      { label: "Starting balance", value: currency(current) },
    ],
    chart: lineChart(projectionPoints(years, (year) => monthlyFutureValue(current, monthlyContribution, monthlyRate, year * 12)), "Savings balance"),
    note: "Monthly compounding · contributions added at month end",
  };
}

function calculateInvestmentFees(values: Record<string, number>): CalculatorResult {
  const initial = values.initial;
  const annualContribution = values.annualContribution;
  const annualReturn = values.annualReturn;
  const feeRate = values.feeRate;
  const years = values.years;
  const monthlyContribution = annualContribution / 12;
  const endingBeforeFees = monthlyFutureValue(initial, monthlyContribution, annualReturn / 100 / 12, years * 12);
  const endingAfterFees = monthlyFutureValue(initial, monthlyContribution, (annualReturn - feeRate) / 100 / 12, years * 12);
  const feeDrag = endingBeforeFees - endingAfterFees;
  return {
    headlineLabel: "Ending value after fees",
    headlineValue: currency(endingAfterFees),
    headlineDetail: `${feeRate.toFixed(2)}% annual fee over ${years} years`,
    stats: [
      { label: "Without the fee", value: currency(endingBeforeFees) },
      { label: "Illustrative fee drag", value: signedCurrency(-feeDrag), tone: "negative" },
      { label: "Annual contribution", value: currency(annualContribution) },
    ],
    chart: lineChart(projectionPoints(years, (year) => monthlyFutureValue(initial, monthlyContribution, (annualReturn - feeRate) / 100 / 12, year * 12)), "Balance after fees"),
    note: "Monthly compounding · return reduced by the annual fee assumption",
  };
}

function calculateMortgage(values: Record<string, number>): CalculatorResult {
  const homePrice = values.homePrice;
  const mortgageInsurance = estimateMortgageInsurance(homePrice, values.downPayment, values.pmiRate ?? 0);
  const loanAmount = mortgageInsurance.loanAmount;
  const months = values.termYears * 12;
  const principalAndInterest = monthlyLoanPayment(loanAmount, values.interestRate, months);
  const propertyTax = homePrice * (values.propertyTaxRate / 100) / 12;
  const insurance = values.homeInsurance / 12;
  const totalMonthly = principalAndInterest + propertyTax + insurance + mortgageInsurance.monthly + values.hoa;
  const totalInterest = Math.max(0, principalAndInterest * months - loanAmount);
  return {
    headlineLabel: "Estimated monthly housing cost",
    headlineValue: currency(totalMonthly, true),
    headlineDetail: `${values.termYears}-year fixed-rate scenario · ${mortgageInsurance.monthly > 0 ? "estimated PMI included" : "PMI not applied"}`,
    stats: [
      { label: "Principal + interest", value: currency(principalAndInterest, true) },
      { label: "Private mortgage insurance", value: currency(mortgageInsurance.monthly, true), detail: mortgageInsurance.monthly > 0 ? "estimated monthly PMI" : "20%+ down or 0% rate" },
      { label: "Total interest", value: currency(totalInterest), tone: "negative" },
      { label: "Amount financed", value: currency(loanAmount) },
    ],
    chart: breakdownChart([
      { label: "Principal + interest", value: principalAndInterest, tone: "accent" },
      { label: "Property tax", value: propertyTax, tone: "blue" },
      { label: "Insurance", value: insurance, tone: "orange" },
      { label: "PMI", value: mortgageInsurance.monthly, tone: "negative" },
      { label: "HOA", value: values.hoa, tone: "purple" },
    ], "Monthly cost breakdown"),
    note: "Principal + interest · property tax · insurance · PMI when applicable · HOA",
  };
}

function calculatePrivateMortgageInsurance(values: Record<string, number>): CalculatorResult {
  const homePrice = values.homePrice;
  const annualRate = values.pmiRate;
  const estimate = estimateMortgageInsurance(homePrice, values.downPayment, annualRate);
  const downPaymentPercent = estimate.downPaymentPercent;
  const chart = [0, 5, 10, 15, 20, 25].map((downPaymentRate) => {
    const scenario = estimateMortgageInsurance(homePrice, homePrice * (downPaymentRate / 100), annualRate);
    return { label: `${downPaymentRate}%`, value: scenario.monthly };
  });
  return {
    headlineLabel: "Estimated monthly PMI",
    headlineValue: currency(estimate.monthly, true),
    headlineDetail: estimate.applies ? `at ${annualRate.toFixed(2)}% annually on the original loan amount` : "not applied at 20% down or more",
    stats: [
      { label: "Original loan amount", value: currency(estimate.loanAmount) },
      { label: "Annual PMI", value: currency(estimate.annual, true), tone: estimate.annual > 0 ? "negative" : "neutral" },
      { label: "Down payment", value: `${downPaymentPercent.toFixed(1)}%`, detail: "of home price" },
    ],
    chart: lineChart(chart, "Monthly PMI by down payment"),
    note: "Conventional-loan illustration · PMI shown below 20% down",
  };
}

function calculateCarLoan(values: Record<string, number>): CalculatorResult {
  const vehiclePrice = values.vehiclePrice;
  const taxes = vehiclePrice * (values.salesTaxRate / 100);
  const financedAmount = Math.max(0, vehiclePrice + taxes + values.fees - values.downPayment - values.tradeIn);
  const monthlyPayment = monthlyLoanPayment(financedAmount, values.interestRate, values.termMonths);
  const totalPayments = monthlyPayment * values.termMonths;
  const totalInterest = Math.max(0, totalPayments - financedAmount);
  return {
    headlineLabel: "Estimated monthly payment",
    headlineValue: currency(monthlyPayment, true),
    headlineDetail: `${values.termMonths}-month loan at ${values.interestRate.toFixed(2)}% APR`,
    stats: [
      { label: "Amount financed", value: currency(financedAmount) },
      { label: "Total loan payments", value: currency(totalPayments) },
      { label: "Total interest", value: currency(totalInterest), tone: "negative" },
    ],
    chart: breakdownChart([
      { label: "Principal", value: financedAmount, tone: "accent" },
      { label: "Interest", value: totalInterest, tone: "negative" },
    ], "Total loan payments"),
    note: "Vehicle price · sales tax · fees · down payment · trade-in",
  };
}

function calculateInvestmentReturn(values: Record<string, number>): CalculatorResult {
  const startingValue = Math.max(1, values.startingValue);
  const endingValue = Math.max(0, values.endingValue);
  const years = Math.max(1, Math.round(values.years));
  const totalReturn = (endingValue / startingValue - 1) * 100;
  const annualizedReturn = (Math.pow(endingValue / startingValue, 1 / years) - 1) * 100;
  return {
    headlineLabel: "Annualized return",
    headlineValue: percentage(annualizedReturn),
    headlineDetail: `over ${years} years from ${currency(startingValue)} to ${currency(endingValue)}`,
    headlineTone: annualizedReturn >= 0 ? "positive" : "negative",
    stats: [
      { label: "Total return", value: percentage(totalReturn), tone: totalReturn >= 0 ? "positive" : "negative" },
      { label: "Dollar change", value: signedCurrency(endingValue - startingValue), tone: endingValue >= startingValue ? "positive" : "negative" },
      { label: "Ending value", value: currency(endingValue) },
    ],
    chart: lineChart(projectionPoints(years, (year) => startingValue * Math.pow(1 + annualizedReturn / 100, year)), "Smoothed value path"),
    note: "CAGR smooths the path into one annualized rate · no deposits or withdrawals",
  };
}

function calculateCreditCardPayoff(values: Record<string, number>): CalculatorResult {
  const monthlyPayment = Math.max(0, values.minimumPayment) + Math.max(0, values.extraPayment);
  const schedule = fixedPayoffSchedule(values.balance, values.annualRate, monthlyPayment);
  const reachedPayoff = Number.isFinite(schedule.months);
  return {
    headlineLabel: "Estimated payoff time",
    headlineValue: displayMonths(schedule.months),
    headlineDetail: reachedPayoff ? `with a ${currency(monthlyPayment, true)} monthly payment` : "the selected payment does not reduce the balance",
    headlineTone: reachedPayoff ? "positive" : "negative",
    stats: [
      { label: "Monthly payment", value: currency(monthlyPayment, true) },
      { label: "Total interest", value: reachedPayoff ? currency(schedule.totalInterest) : "Not reached", tone: reachedPayoff ? "negative" : "neutral" },
      { label: "Total paid", value: reachedPayoff ? currency(schedule.totalPaid) : "Not reached" },
    ],
    chart: payoffChart(schedule, "Remaining balance"),
    note: "Fixed payment · monthly interest · no new charges or fees",
  };
}

function calculateDebtPayoff(values: Record<string, number>): CalculatorResult {
  const debts: DebtInput[] = [
    { balance: values.debt1Balance, annualRate: values.debt1Rate, minimumPayment: values.debt1Payment },
    { balance: values.debt2Balance, annualRate: values.debt2Rate, minimumPayment: values.debt2Payment },
    { balance: values.debt3Balance, annualRate: values.debt3Rate, minimumPayment: values.debt3Payment },
  ];
  const avalanche = debtPayoffSchedule(debts, values.extraPayment, "avalanche");
  const snowball = debtPayoffSchedule(debts, values.extraPayment, "snowball");
  const reachedPayoff = Number.isFinite(avalanche.months);
  const interestDifference = snowball.totalInterest - avalanche.totalInterest;
  return {
    headlineLabel: "Avalanche payoff time",
    headlineValue: displayMonths(avalanche.months),
    headlineDetail: reachedPayoff ? "highest-rate balance receives the extra payment first" : "the selected monthly budget does not reach payoff",
    headlineTone: reachedPayoff ? "positive" : "negative",
    stats: [
      { label: "Snowball payoff time", value: displayMonths(snowball.months) },
      { label: "Avalanche interest", value: reachedPayoff ? currency(avalanche.totalInterest) : "Not reached", tone: reachedPayoff ? "negative" : "neutral" },
      { label: "Interest difference", value: reachedPayoff && Number.isFinite(snowball.totalInterest) ? signedCurrency(interestDifference) : "Not reached", tone: interestDifference >= 0 ? "positive" : "negative", detail: "avalanche vs. snowball" },
    ],
    chart: payoffChart(avalanche, "Avalanche remaining balance"),
    note: "Minimums plus extra payment · no new charges · illustrative comparison only",
  };
}

function calculateRetirement(values: Record<string, number>): CalculatorResult {
  const years = Math.min(MAX_SCENARIO_YEARS, Math.max(1, Math.round(values.retirementAge - values.currentAge)));
  let balance = Math.max(0, values.currentBalance);
  let salary = Math.max(0, values.annualSalary);
  let employeeContributions = 0;
  let employerContributions = 0;
  const balances = [balance];
  for (let year = 0; year < years; year += 1) {
    const employee = salary * (Math.max(0, values.contributionRate) / 100);
    const employer = employee * (Math.max(0, values.employerMatchRate) / 100);
    employeeContributions += employee;
    employerContributions += employer;
    balance = balance * (1 + Math.max(0, values.annualReturn) / 100) + employee + employer;
    salary *= 1 + Math.max(0, values.salaryGrowth) / 100;
    balances.push(balance);
  }
  const growth = Math.max(0, balance - values.currentBalance - employeeContributions - employerContributions);
  return {
    headlineLabel: "Illustrative retirement balance",
    headlineValue: currency(balance),
    headlineDetail: `at age ${Math.round(values.currentAge) + years} after ${years} years`,
    headlineTone: "positive",
    stats: [
      { label: "Your contributions", value: currency(employeeContributions) },
      { label: "Employer contributions", value: currency(employerContributions), tone: "positive" },
      { label: "Growth in scenario", value: currency(growth), tone: "positive" },
    ],
    chart: lineChart(projectionPoints(years, (year) => balances[Math.min(years, Math.round(year))] ?? balance), "Projected balance"),
    note: "Annual contributions and compounding · nominal balance · no taxes or withdrawals",
  };
}

function calculateNetWorth(values: Record<string, number>): CalculatorResult {
  const assets = values.cash + values.investments + values.retirement + values.realEstate + values.vehicles + values.otherAssets;
  const liabilities = values.mortgageDebt + values.creditCardDebt + values.studentLoanDebt + values.otherDebts;
  const netWorth = assets - liabilities;
  const liquidAssets = values.cash + values.investments;
  return {
    headlineLabel: "Estimated net worth",
    headlineValue: displayCurrency(netWorth),
    headlineDetail: "assets minus liabilities",
    headlineTone: netWorth >= 0 ? "positive" : "negative",
    stats: [
      { label: "Total assets", value: currency(assets) },
      { label: "Total liabilities", value: currency(liabilities), tone: liabilities > 0 ? "negative" : "neutral" },
      { label: "Liquid assets", value: currency(liquidAssets) },
    ],
    chart: breakdownChart([
      { label: "Assets", value: assets, tone: "accent" },
      { label: "Liabilities", value: liabilities, tone: "negative" },
    ], "Balance sheet snapshot"),
    note: "Gross asset values minus stated debts · no taxes, fees, or valuation model",
  };
}

function calculateEmergencyFund(values: Record<string, number>): CalculatorResult {
  const target = Math.max(0, values.monthlyExpenses) * Math.max(1, Math.round(values.targetMonths));
  const currentSavings = Math.max(0, values.currentSavings);
  const shortfall = Math.max(0, target - currentSavings);
  const monthlyContribution = Math.max(0, values.monthlyContribution);
  const monthsToTarget = shortfall === 0 ? 0 : monthlyContribution > 0 ? Math.ceil(shortfall / monthlyContribution) : Number.POSITIVE_INFINITY;
  const chartMonths = Math.max(1, Math.round(values.targetMonths), Number.isFinite(monthsToTarget) ? monthsToTarget : 1);
  return {
    headlineLabel: "Illustrative reserve target",
    headlineValue: currency(target),
    headlineDetail: `${Math.round(values.targetMonths)} months of essential expenses`,
    headlineTone: "positive",
    stats: [
      { label: "Current savings", value: currency(currentSavings) },
      { label: "Savings shortfall", value: currency(shortfall), tone: shortfall > 0 ? "negative" : "positive" },
      { label: "Time to target", value: displayMonths(monthsToTarget) },
    ],
    chart: lineChart(monthProjectionPoints(chartMonths, (month) => Math.min(target, currentSavings + monthlyContribution * month)), "Emergency savings path"),
    note: "No interest assumed · target months are your chosen scenario, not a universal rule",
  };
}

function mortgageMonthlyCost(homePrice: number, values: Record<string, number>) {
  const estimate = estimateMortgageInsurance(homePrice, values.downPayment, values.pmiRate);
  const principalAndInterest = monthlyLoanPayment(estimate.loanAmount, values.interestRate, values.termYears * 12);
  const propertyTax = homePrice * (values.propertyTaxRate / 100) / 12;
  const insurance = values.homeInsurance / 12;
  const total = principalAndInterest + propertyTax + insurance + estimate.monthly + values.hoa;
  return { total, principalAndInterest, estimate };
}

function calculateMortgageAffordability(values: Record<string, number>): CalculatorResult {
  const housingBudget = Math.max(0, values.monthlyIncome * (values.housingDti / 100) - values.monthlyDebts);
  let low = 0;
  let high = 10_000_000;
  for (let index = 0; index < 42; index += 1) {
    const midpoint = (low + high) / 2;
    if (mortgageMonthlyCost(midpoint, values).total <= housingBudget) low = midpoint;
    else high = midpoint;
  }
  const affordablePrice = Math.max(0, low);
  const affordableCost = mortgageMonthlyCost(affordablePrice, values);
  const priceSteps = [0, 0.25, 0.5, 0.75, 1];
  return {
    headlineLabel: "Illustrative affordable home price",
    headlineValue: currency(affordablePrice),
    headlineDetail: `under a ${values.housingDti.toFixed(1)}% housing DTI scenario`,
    headlineTone: "positive",
    stats: [
      { label: "Housing budget", value: currency(housingBudget, true) },
      { label: "Principal + interest", value: currency(affordableCost.principalAndInterest, true) },
      { label: "Other monthly debt", value: currency(values.monthlyDebts, true) },
    ],
    chart: lineChart(priceSteps.map((step) => ({ label: `${Math.round(step * 100)}%`, value: mortgageMonthlyCost(affordablePrice * step, values).total })), "Monthly cost across price range"),
    note: "Gross-income scenario · lender underwriting and costs outside the inputs are not modeled",
  };
}

function calculateMortgageRefinance(values: Record<string, number>): CalculatorResult {
  const currentMonths = Math.max(1, Math.round(values.remainingTermYears * 12));
  const newMonths = Math.max(1, Math.round(values.newTermYears * 12));
  const currentPayment = monthlyLoanPayment(values.currentBalance, values.currentRate, currentMonths);
  const newPayment = monthlyLoanPayment(values.currentBalance, values.newRate, newMonths);
  const monthlySavings = currentPayment - newPayment;
  const breakEvenMonths = monthlySavings > 0 ? values.closingCosts / monthlySavings : Number.POSITIVE_INFINITY;
  const currentInterest = Math.max(0, currentPayment * currentMonths - values.currentBalance);
  const newInterest = Math.max(0, newPayment * newMonths - values.currentBalance);
  const interestDifference = currentInterest - newInterest;
  return {
    headlineLabel: "Estimated break-even",
    headlineValue: Number.isFinite(breakEvenMonths) ? displayMonths(breakEvenMonths) : "No break-even",
    headlineDetail: monthlySavings > 0 ? `${currency(monthlySavings, true)} estimated monthly savings` : "the new payment is not lower",
    headlineTone: monthlySavings > 0 ? "positive" : "negative",
    stats: [
      { label: "Current payment", value: currency(currentPayment, true) },
      { label: "New payment", value: currency(newPayment, true) },
      { label: "Interest difference", value: signedCurrency(interestDifference), tone: interestDifference >= 0 ? "positive" : "negative", detail: "before closing costs" },
    ],
    chart: lineChart([
      { label: "Current", value: currentPayment },
      { label: "New", value: newPayment },
    ], "Monthly principal + interest"),
    note: "Fixed-rate comparison · closing costs divided by monthly savings",
  };
}

function calculateRentVsBuy(values: Record<string, number>): CalculatorResult {
  const years = Math.min(MAX_SCENARIO_YEARS, Math.max(1, Math.round(values.yearsToCompare)));
  const months = years * 12;
  const loanAmount = Math.max(0, values.homePrice - Math.min(values.homePrice, Math.max(0, values.downPayment)));
  const monthlyPrincipalAndInterest = monthlyLoanPayment(loanAmount, values.interestRate, values.termYears * 12);
  const monthlyOwnershipExtras = values.homePrice * (values.propertyTaxRate / 100) / 12 + values.homeInsurance / 12 + values.homePrice * (values.maintenanceRate / 100) / 12;
  const rentGrowth = values.rentGrowth / 100;
  const rentTotal = rentGrowth === 0
    ? values.monthlyRent * 12 * years
    : values.monthlyRent * 12 * (Math.pow(1 + rentGrowth, years) - 1) / rentGrowth;
  const initialDownPayment = Math.min(values.homePrice, Math.max(0, values.downPayment));
  const ownershipCash = initialDownPayment + months * (monthlyPrincipalAndInterest + monthlyOwnershipExtras);
  const futureHomeValue = values.homePrice * Math.pow(1 + values.appreciationRate / 100, years);
  const remainingBalance = remainingLoanBalance(loanAmount, values.interestRate, values.termYears * 12, months);
  const homeEquity = futureHomeValue - remainingBalance;
  const sellingCosts = futureHomeValue * (values.sellingCostRate / 100);
  const netBuyCost = ownershipCash - homeEquity + sellingCosts;
  const differenceVsRent = rentTotal - netBuyCost;
  return {
    headlineLabel: "Estimated net cost of buying",
    headlineValue: displayCurrency(netBuyCost),
    headlineDetail: `over ${years} years after modeled equity and selling costs`,
    stats: [
      { label: "Renting cost", value: currency(rentTotal) },
      { label: "Home equity", value: displayCurrency(homeEquity) },
      { label: "Difference vs. rent", value: signedCurrency(differenceVsRent), tone: differenceVsRent >= 0 ? "positive" : "negative", detail: "positive means buying costs less in this scenario" },
    ],
    chart: breakdownChart([
      { label: "Renting", value: rentTotal, tone: "blue" },
      { label: "Buying after equity", value: Math.max(0, netBuyCost), tone: "accent" },
    ], "Scenario cost comparison"),
    note: "Rent growth · mortgage · maintenance · appreciation · selling costs",
  };
}

function calculateStudentLoanPayoff(values: Record<string, number>): CalculatorResult {
  const monthlyPayment = Math.max(0, values.monthlyPayment) + Math.max(0, values.extraPayment);
  const schedule = fixedPayoffSchedule(values.balance, values.annualRate, monthlyPayment);
  const reachedPayoff = Number.isFinite(schedule.months);
  return {
    headlineLabel: "Estimated payoff time",
    headlineValue: displayMonths(schedule.months),
    headlineDetail: reachedPayoff ? `with a ${currency(monthlyPayment, true)} monthly payment` : "the selected payment does not reduce the balance",
    headlineTone: reachedPayoff ? "positive" : "negative",
    stats: [
      { label: "Monthly payment", value: currency(monthlyPayment, true) },
      { label: "Total interest", value: reachedPayoff ? currency(schedule.totalInterest) : "Not reached", tone: reachedPayoff ? "negative" : "neutral" },
      { label: "Total paid", value: reachedPayoff ? currency(schedule.totalPaid) : "Not reached" },
    ],
    chart: payoffChart(schedule, "Remaining loan balance"),
    note: "Fixed-rate scenario · no forgiveness, deferment, fees, or new borrowing",
  };
}

export function calculateCalculator(slug: CalculatorSlug, values: Record<string, number>): CalculatorResult {
  switch (slug) {
    case "etf-stock-overlap": return calculateEtfOverlap(values);
    case "portfolio-concentration": return calculatePortfolioConcentration(values);
    case "portfolio-drawdown": return calculatePortfolioDrawdown(values);
    case "compound-interest": return calculateCompoundInterest(values);
    case "inflation": return calculateInflation(values);
    case "savings-goal": return calculateSavingsGoal(values);
    case "investment-fees": return calculateInvestmentFees(values);
    case "mortgage-payment": return calculateMortgage(values);
    case "private-mortgage-insurance": return calculatePrivateMortgageInsurance(values);
    case "car-loan-payment": return calculateCarLoan(values);
    case "investment-return": return calculateInvestmentReturn(values);
    case "credit-card-payoff": return calculateCreditCardPayoff(values);
    case "debt-payoff": return calculateDebtPayoff(values);
    case "retirement-401k": return calculateRetirement(values);
    case "net-worth": return calculateNetWorth(values);
    case "emergency-fund": return calculateEmergencyFund(values);
    case "mortgage-affordability": return calculateMortgageAffordability(values);
    case "mortgage-refinance": return calculateMortgageRefinance(values);
    case "rent-vs-buy": return calculateRentVsBuy(values);
    case "student-loan-payoff": return calculateStudentLoanPayoff(values);
  }
}
